import { useCallback, useState } from 'react';
import { View, Text, FlatList, StyleSheet, Switch, TouchableOpacity } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useFocusEffect } from 'expo-router';
import { Colors } from '@/constants/colors';
import { useFavoritesStore } from '@/store/favorites';
import { useNotificationsStore } from '@/store/notifications';
import { EmptyState } from '@/components/EmptyState';
import { UserPreferences, NotifEvent } from '@/types';
import { sendPushTest, syncPushRegistration } from '@/services/notifications';

const EVENT_TOGGLES: { key: keyof UserPreferences; emoji: string; label: string; sub: string }[] = [
  { key: 'notifyGameStart',   emoji: '🎮', label: 'Início de jogo',       sub: 'Quando a partida começa' },
  { key: 'notifyGoals',       emoji: '⚽', label: 'Gols',                  sub: 'Todo gol marcado' },
  { key: 'notifyYellowCards', emoji: '🟨', label: 'Cartões amarelos',     sub: 'Pode gerar muitas notificações' },
  { key: 'notifyRedCards',    emoji: '🟥', label: 'Cartões vermelhos',    sub: 'Expulsões' },
  { key: 'notifySubstitutions', emoji: '🔁', label: 'Substituições',       sub: 'Trocas durante a partida' },
  { key: 'notifyHalftime',    emoji: '⏸️', label: 'Fim do 1º tempo',      sub: 'Intervalo' },
  { key: 'notifySecondHalf',  emoji: '▶️', label: 'Início do 2º tempo',   sub: 'Volta do intervalo' },
  { key: 'notifyGameEnd',     emoji: '🏁', label: 'Fim de jogo',          sub: 'Resultado final' },
  { key: 'notifyScoreChange', emoji: '🏀', label: 'Pontos (NBA/NFL)',     sub: 'Mudança de placar no basquete/futebol americano' },
];

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export default function AlertasScreen() {
  const router = useRouter();
  const { preferences, updatePreferences } = useFavoritesStore();
  const { events, clear } = useNotificationsStore();
  const markAllRead = useNotificationsStore((s) => s.markAllRead);
  const [pushTestStatus, setPushTestStatus] = useState('Pronto para teste');

  // Zera o badge de não-lidos sempre que a aba é aberta
  useFocusEffect(useCallback(() => { markAllRead(); }, [markAllRead]));

  function openGame(ev: NotifEvent) {
    router.push(`/match/${ev.gameId}?sport=${ev.sport}`);
  }

  async function testPush() {
    setPushTestStatus('Enviando...');
    const result = await sendPushTest();
    setPushTestStatus(result.message);
  }

  return (
    <SafeAreaView style={styles.safe} edges={['top']}>
      <View style={styles.header}>
        <Text style={styles.title}>🔔 Alertas</Text>
        {events.length > 0 && (
          <TouchableOpacity onPress={clear} style={styles.clearBtn}>
            <Text style={styles.clearBtnText}>Limpar histórico</Text>
          </TouchableOpacity>
        )}
      </View>

      <FlatList
        data={events}
        keyExtractor={(e) => e.id}
        renderItem={({ item }) => (
          <TouchableOpacity style={styles.eventRow} onPress={() => openGame(item)} activeOpacity={0.7}>
            <Text style={styles.eventEmoji}>{item.emoji}</Text>
            <View style={styles.eventInfo}>
              <Text style={styles.eventTitle} numberOfLines={1}>{item.title}</Text>
              <Text style={styles.eventBody} numberOfLines={1}>{item.body}</Text>
            </View>
            <Text style={styles.eventTime}>{relativeTime(item.ts)}</Text>
          </TouchableOpacity>
        )}
        ListHeaderComponent={
          <>
            <Text style={styles.sectionTitle}>PUSH 24H</Text>
            <View style={styles.card}>
              <View style={styles.pushStatusRow}>
                <View style={styles.pushStatusTextBlock}>
                  <Text style={styles.pushStatusTitle}>Notificacoes remotas</Text>
                  <Text style={styles.pushStatusSub}>{pushTestStatus}</Text>
                </View>
                <TouchableOpacity style={styles.testButton} onPress={testPush} activeOpacity={0.75}>
                  <Text style={styles.testButtonText}>Testar push</Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Configurações por evento */}
            <Text style={styles.sectionTitle}>NOTIFICAR QUANDO HOUVER</Text>
            <View style={styles.card}>
              {EVENT_TOGGLES.map(({ key, emoji, label, sub }, i) => (
                <View key={key} style={[styles.toggleRow, i < EVENT_TOGGLES.length - 1 && styles.toggleBorder]}>
                  <Text style={styles.toggleEmoji}>{emoji}</Text>
                  <View style={styles.toggleText}>
                    <Text style={styles.toggleLabel}>{label}</Text>
                    <Text style={styles.toggleSub}>{sub}</Text>
                  </View>
                  <Switch
                    value={preferences[key] as boolean}
                    onValueChange={(v) => {
                      updatePreferences({ [key]: v });
                      setTimeout(() => syncPushRegistration().catch(() => {}), 0);
                    }}
                    trackColor={{ false: Colors.border, true: Colors.futebol }}
                    thumbColor={Colors.white}
                  />
                </View>
              ))}
            </View>

            {/* Cabeçalho do histórico */}
            <Text style={styles.sectionTitle}>
              HISTÓRICO {events.length > 0 ? `(${events.length})` : ''}
            </Text>
          </>
        }
        ListEmptyComponent={
          <EmptyState
            icon="🔔"
            title="Nenhum evento ainda"
            subtitle="Os lances de jogos ao vivo (gols, cartões, início/fim) aparecerão aqui em tempo real"
          />
        }
        contentContainerStyle={styles.list}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: Colors.background },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 4,
  },
  title: { color: Colors.text, fontSize: 22, fontWeight: '800' },
  clearBtn: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: 'rgba(239,68,68,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(239,68,68,0.25)',
  },
  clearBtnText: { color: '#f87171', fontSize: 11, fontWeight: '600' },

  sectionTitle: {
    color: Colors.textMuted,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 10,
  },
  card: {
    marginHorizontal: 16,
    backgroundColor: Colors.card,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.cardBorder,
    overflow: 'hidden',
  },
  pushStatusRow: {
    minHeight: 70,
    paddingHorizontal: 14,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  pushStatusTextBlock: { flex: 1 },
  pushStatusTitle: { color: Colors.text, fontSize: 14, fontWeight: '800' },
  pushStatusSub: { color: Colors.textMuted, fontSize: 11, marginTop: 3 },
  testButton: {
    height: 36,
    paddingHorizontal: 12,
    borderRadius: 9,
    backgroundColor: 'rgba(77,255,63,0.14)',
    borderWidth: 1,
    borderColor: 'rgba(77,255,63,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  testButtonText: {
    color: Colors.futebol,
    fontSize: 12,
    fontWeight: '900',
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 12,
  },
  toggleBorder: { borderBottomWidth: 1, borderBottomColor: Colors.border },
  toggleEmoji: { fontSize: 20, width: 26, textAlign: 'center' },
  toggleText: { flex: 1 },
  toggleLabel: { color: Colors.text, fontSize: 14, fontWeight: '600' },
  toggleSub: { color: Colors.textMuted, fontSize: 11, marginTop: 1 },

  eventRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  eventEmoji: { fontSize: 22, width: 28, textAlign: 'center' },
  eventInfo: { flex: 1 },
  eventTitle: { color: Colors.text, fontSize: 13, fontWeight: '700' },
  eventBody: { color: Colors.textSecondary, fontSize: 12, marginTop: 1 },
  eventTime: { color: Colors.textMuted, fontSize: 11 },

  list: { paddingBottom: 32 },
});
